import {
  CaretDownFilled,
  CaretLeftFilled,
  DeleteOutlined,
  FormOutlined,
  PrinterOutlined,
  UserAddOutlined,
} from '@ant-design/icons'
import {
  Button,
  Checkbox,
  Dropdown,
  Form,
  Menu,
  Modal,
  Progress,
  Tag,
  message,
} from 'antd'
import { inject, observer } from 'mobx-react'
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { flushSync } from 'react-dom'
import { Helmet } from 'react-helmet'
import { blue } from '../../../color'
import AccessControlAction from '../../../components/AccessControlAction'
import { MenuDotsBoldIcon } from '../../../components/AppIcon'
import AppPageHeader from '../../../components/AppPageHeader'
import { EmptyText } from '../../../components/Common/CellText'
import FileItem from '../../../components/Common/FileItemComponent/FileItem'
import ContentBlockWrapper from '../../../components/ContentBlockWrapper'
import FeedbackSign from '../../../components/PopupSign/FeedbackSign'
import {
  ACL_ACTION_TYPE,
  ASSIGNEE_TYPE,
  VIEWLOG_DATE,
} from '../../../constants'
import DashboardLayout from '../../../layouts/DashboardLayout'
import { FeedbackProcedureRequest } from '../../../requests/FeedbackProcedureRequest'
import utils from '../../../utils'
import { renderElementToPdfFile } from '../../../utils/pdfcanvasutils '
import { UserListWrapper } from '../InternalDocumentIncomingDocDetailPage/InternalDocumentIncomingDocDetailPageStyled'
import { PdfEsignUltil } from './components/PDFMerge'
import PopoverWatchTime from './components/PopoverWatchTime'
import ExportPdf from './ExportPdf/ExportPdf'
import ExportPdfV2 from './ExportPdf/ExportPdfV2'
import ExportDocx from './ExportWord/ExportWord'
import FeedbackBoardAddPerson from './FeedbackBoardAddPerson'
import {
  BlockLeft,
  BlockRight,
  ContentWrapper,
  DocumentInfoList,
} from './FeedbackBoardPageStyled'
import {
  FEEDBACK_BOARD_TAB,
  getFeedbackTabFromData,
  getFeedbackTabFromRouteParam,
  getPlykSuffixByTab,
  isDelegateFeedbackTab,
  toDisplayDocumentNumber,
} from './feedbackBoardType'
import FeedbackBoardUpdate from './FeedbackBoardUpdate '
import FeedbackCommentSection from './FeedbackCommentSection'

const normalizePositionValue = value =>
  (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\u0111/g, 'd')
    .replace(/\u0110/g, 'D')
    .toUpperCase()
    .trim()

const getVietnameseGivenNameKey = fullName => {
  const normalized = normalizePositionValue(fullName)
  if (!normalized) return ''
  const nameParts = normalized.split(/\s+/)
  return nameParts[nameParts.length - 1] || normalized
}

const compareVietnameseName = (nameA, nameB) => {
  const givenNameA = getVietnameseGivenNameKey(nameA)
  const givenNameB = getVietnameseGivenNameKey(nameB)

  if (givenNameA !== givenNameB) {
    return givenNameA.localeCompare(givenNameB)
  }

  return normalizePositionValue(nameA).localeCompare(
    normalizePositionValue(nameB)
  )
}

const getVotingPositionPriority = user => {
  const positionCode = normalizePositionValue(
    user?.position?.code ||
      user?.positionCode ||
      user?.position_code ||
      user?.positionCodeName
  )
  const positionName = normalizePositionValue(
    user?.position?.name ||
      user?.position_name ||
      user?.positionName ||
      (typeof user?.position === 'string' ? user.position : '')
  )

  if (
    ['CTHDTV', 'CTHDQT', 'CT'].includes(positionCode) ||
    positionCode.startsWith('CTHD') ||
    positionName.includes('CHU TICH HOI DONG QUAN TRI') ||
    positionName.includes('CHU TICH HDQT')
  ) {
    return 1
  }

  if (
    positionCode === 'TGD' ||
    positionName.includes('TONG GIAM DOC') ||
    positionName === 'TGD'
  ) {
    return 2
  }

  if (
    ['TVHDTV', 'TVHDQT'].includes(positionCode) ||
    positionCode.startsWith('TVHD') ||
    positionName.includes('THANH VIEN HOI DONG QUAN TRI') ||
    positionName.includes('THANH VIEN HDQT')
  ) {
    return 3
  }

  return 99
}

const HDQT_VOTING_ORDER_USERNAMES = [
  'tinhnc',
  'sonla',
  'chungnd',
  'ducdt',
  'duongdh',
]

const HDQT_VOTING_ORDER_NAMES = [
  'NGUYEN CANH TINH',
  'LE ANH SON',
  'NGUYEN DINH CHUNG',
  'DO TIEN DUC',
  'DO HUNG DUONG',
]

const hdqtUsernameOrderMap = new Map(
  HDQT_VOTING_ORDER_USERNAMES.map((username, index) => [username, index + 1])
)
const hdqtNameOrderMap = new Map(
  HDQT_VOTING_ORDER_NAMES.map((name, index) => [name, index + 1])
)

const getHdqtVotingOrder = (username, fullName) => {
  if (username && hdqtUsernameOrderMap.has(username)) {
    return hdqtUsernameOrderMap.get(username)
  }

  const normalizedName = normalizePositionValue(fullName)
  if (normalizedName && hdqtNameOrderMap.has(normalizedName)) {
    return hdqtNameOrderMap.get(normalizedName)
  }

  return null
}

const getUserPositionName = user =>
  user?.position?.name ||
  user?.position_name ||
  user?.positionName ||
  user?.position_detail?.name ||
  user?.position_detail?.position_name ||
  user?.positionNameInternal ||
  (typeof user?.position === 'string' ? user.position : '') ||
  ''

const FeedbackBoardDetail = props => {
  const {
    selectPeopleStore,
    fileStore,
    userGroupStore,
    signedDocumentStore,
    authenticationStore,
    loadingAnimationStore,
    FeedbackProcedureStore,
    FeedbackCommentStore,
    departmentStore,
    selectUserStore,
    userStore,
    match,
    history,
    location,
    aclStore,
  } = props
  const EDITOR_REF = useRef(null)
  // FIX Bug2: flag ngăn useEffect ghi đè state ngay sau khi submit
  const skipSyncRef = useRef(false)
  const [form] = Form.useForm()
  const { feedbackId, orther } = match.params
  const { currentUser } = authenticationStore
  const { feedbackDetailData } = FeedbackProcedureStore
  const { documentAttachment } = fileStore
  const { allDataGroupList } = userGroupStore
  const { feedbackCommentDataDetailV2 } = FeedbackCommentStore
  const { departmentWithUserList, departmentWithUserListv2 } = selectPeopleStore
  const [asigneeData, setAsigneeData] = useState([])
  const [isEditModalVisible, setIsEditModalVisible] = useState(false)
  const [isAddPersonModalVisible, setIsAddPersonModalVisible] = useState(false)
  const [exportDocx, setExportDocx] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(true)
  const [isLegalBasisOpen, setIsLegalBasisOpen] = useState(true)
  const [isPurposeOpen, setIsPurposeOpen] = useState(true)
  const [isProcessOpen, setIsProcessOpen] = useState(true)
  const [selectedIndex, setSelectedIndex] = useState(null)
  const [indexCore, setIndexCore] = useState(null)

  useEffect(() => {
    // Reset selection state when feedbackId changes
    setSelectedIndex(null)
    setIndexCore(null)
    setIsMenuOpen(true)
    setIsLegalBasisOpen(true)
    setIsPurposeOpen(true)
    setIsProcessOpen(true)
  }, [feedbackId])
  const [isVisiblePopupSign, setIsVisiblePopupSign] = useState(false)
  const [selectedOption, setSelectedOption] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isVoting, setIsVoting] = useState(false)
  const [currentComment, setCurrentComment] = useState('')
  const [editorContent, setEditorContent] = useState(null)
  const [uploadedFiles, setUploadedFiles] = useState([])
  const [selectedOptions, setSelectedOptions] = useState({})
  const [fetchedUserDetails, setFetchedUserDetails] = useState({})
  const routeFeedbackTab = getFeedbackTabFromRouteParam(orther)
  const currentFeedbackTab = useMemo(() => {
    if (
      orther &&
      !['feedback-detail', FEEDBACK_BOARD_TAB.HDQT].includes(
        orther.toLowerCase()
      )
    ) {
      return routeFeedbackTab
    }

    return getFeedbackTabFromData({
      boardType:
        feedbackDetailData?.boardType || feedbackDetailData?.board_type,
      documentNumber: feedbackDetailData?.documentNumber,
    })
  }, [
    orther,
    routeFeedbackTab,
    feedbackDetailData?.boardType,
    feedbackDetailData?.board_type,
    feedbackDetailData?.documentNumber,
  ])
  const isDelegateTab = isDelegateFeedbackTab(currentFeedbackTab)
  const plykSuffix = getPlykSuffixByTab(currentFeedbackTab)

  const updateCurrentComment = mergedComments => {
    setCurrentComment(mergedComments)
  }

  const fetchDetailComment = async id => {
    try {
      await FeedbackCommentStore.getFeedbackCommentById(id)
    } catch (error) {
      loadingAnimationStore.showSpinner(false)
    }
  }
  const isHandler = useMemo(() => {
    return feedbackDetailData?.assignees?.some(
      assignee =>
        assignee.permission === 'PIC' &&
        assignee.assignee_code === currentUser?.username
    )
  }, [feedbackDetailData?.assignees, currentUser?.username])

  // Hoặc nếu user trong department có permission PIC
  const isHandlerInDepartment = useMemo(() => {
    const picAssignees =
      feedbackDetailData?.assignees?.filter(
        assignee => assignee.permission === 'PIC'
      ) || []

    return picAssignees.some(assignee => {
      if (assignee.assignee_type === 'USER') {
        return assignee.assignee_code === currentUser?.username
      } else if (assignee.assignee_type === 'DPT') {
        const department = departmentWithUserListv2?.find(
          dept => dept.code === assignee.assignee_code
        )
        return department?.users?.some(
          user => user.user_name === currentUser?.username
        )
      }
      return false
    })
  }, [
    feedbackDetailData?.assignees,
    departmentWithUserListv2,
    currentUser?.username,
  ])

  const canVote = isHandler || isHandlerInDepartment

  // Sync selectedOptions từ feedbackCommentDataDetailV2 (data source chính xác, có isDeleted)
  // Chỉ sync khi feedbackCommentDataDetailV2.id === selectedIndex để tránh flash data cũ
  // khi vừa click item mới nhưng fetch chưa xong
  useEffect(() => {
    if (skipSyncRef.current) {
      skipSyncRef.current = false
      return
    }

    const commentId = feedbackCommentDataDetailV2?.id
    if (
      !feedbackCommentDataDetailV2?.feedbackCommentDetails ||
      !currentUser?.username ||
      !commentId
    )
      return

    // Guard: data phải thuộc đúng comment đang chọn, nếu không thì bỏ qua
    if (commentId !== selectedIndex) return

    const latestVote = feedbackCommentDataDetailV2.feedbackCommentDetails
      .filter(
        detail =>
          detail.feedbackUsername === currentUser?.username &&
          detail.voteType &&
          detail.fileId &&
          !detail.isDeleted
      )
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      )[0]

    setSelectedOptions(prev => ({
      ...prev,
      [commentId]: latestVote ? latestVote.voteType : prev[commentId],
    }))
  }, [
    feedbackCommentDataDetailV2?.feedbackCommentDetails,
    feedbackCommentDataDetailV2?.id,
    currentUser?.username,
  ])

  const handleUploadChange = info => {
    const fileList = info.fileList.map(file => ({
      uid: file.uid,
      name: file.name,
      status: file.status,
      originFileObj: file.originFileObj,
    }))
    setUploadedFiles(fileList)
  }

  // Helper function chỉ lấy comment của người đang biểu quyết
  const getAllComments = () => {
    const existingComments = (
      feedbackCommentDataDetailV2?.feedbackCommentDetails || []
    )
      .filter(c => c.feedbackUsername === currentUser?.username && !c.isDeleted)
      .map(c => c.detailFeedbackComment)
      .filter(comment => comment && comment.trim() !== '')

    const newComment = EDITOR_REF.current?.editor?.getData() || ''

    const allComments =
      newComment.trim() !== ''
        ? [...existingComments, newComment]
        : existingComments

    return allComments.join('<br/>')
  }

  const handleVoteSubmit = async () => {
    setIsVoting(true)
    const currentSelectedOption = selectedOptions[selectedIndex]

    if (utils.isNullish(currentSelectedOption)) {
      message.error('Vui lòng chọn lựa chọn biểu quyết!')
      setIsVoting(false)
      return
    }

    // Đặt cờ NGAY ĐẦU để chặn useEffect ghi đè suốt toàn bộ quá trình submit
    skipSyncRef.current = true

    try {
      const newComment = EDITOR_REF.current?.editor?.getData() || ''

      // selectedIndex là nguồn sự thật: luôn dùng nó thay vì feedbackCommentDataDetailV2.id
      // feedbackCommentDataDetailV2.id có thể trỏ sai nội dung do các async call trước đó
      const targetCommentId = Number(selectedIndex)

      // Chỉ fetch lại nếu feedbackCommentDataDetailV2 đang trỏ sai content
      // (tránh fetch lại khi data đã fresh từ handleCommentSubmit - gây backend cache lag)
      let currentDetails
      if (
        feedbackCommentDataDetailV2 &&
        Number(feedbackCommentDataDetailV2.id) === targetCommentId
      ) {
        // Data đã đúng và fresh (vừa được refresh bởi handleCommentSubmit hoặc handleItemClick)
        currentDetails = feedbackCommentDataDetailV2.feedbackCommentDetails || []
      } else {
        // Mismatch: feedbackCommentDataDetailV2 trỏ sai content → fetch đúng
        await FeedbackCommentStore.getFeedbackCommentById(targetCommentId)
        currentDetails =
          FeedbackCommentStore.feedbackCommentDataDetailV2
            ?.feedbackCommentDetails || []
      }

      const existingComments = currentDetails
        .filter(
          c =>
            !c.isDeleted &&
            c.feedbackUsername === currentUser?.username &&
            c.detailFeedbackComment &&
            c.detailFeedbackComment.trim() !== ''
        )
        .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
        .map(c => c.detailFeedbackComment)

      const mergedComments =
        newComment.trim() !== ''
          ? [...existingComments, newComment].join('<br/>')
          : existingComments.join('<br/>')

      // Refresh feedbackDetailData để ExportPdfV2 có data mới nhất cho TẤT CẢ các nội dung,
      // không chỉ nội dung đang chọn. Ví dụ: user thêm comment Content 1, rồi biểu quyết
      // Content 2 → ExportPdfV2 cần feedbackDetailData mới để hiện comment Content 1 đúng.
      await FeedbackProcedureStore.getElectronicDocumentById(feedbackId)

      // flushSync: ép React flush ĐỒNG BỘ cả hai update:
      // 1. MobX-triggered re-render từ feedbackDetailData vừa thay đổi
      // 2. setCurrentComment cho nội dung đang chọn
      // → ExportPdfV2 render với data đầy đủ nhất trước khi PDF capture
      flushSync(() => {
        setCurrentComment(mergedComments)
      })

      // Upload các file đính kèm
      let uploadFileIds = []
      let uploadFileNames = []

      if (uploadedFiles.length > 0) {
        for (const file of uploadedFiles) {
          if (file.originFileObj) {
            const formData = new FormData()
            formData.append('file', file.originFileObj)
            const uploadResponse = await fileStore.uploadFile(formData)
            uploadFileIds.push(uploadResponse.data.file_id)
            uploadFileNames.push(uploadResponse.data.file_name)
          }
        }
      }

      // Generate PDF for Part 1
      const ts = new Date().getTime()
      const filePart1 = await renderElementToPdfFile(
        'divToPrintPart1',
        `${currentUser?.code}_${ts}_part1`
      )
      const formDataPart1 = new FormData()
      formDataPart1.append('file', filePart1)
      const responseSignDocPart1 = await fileStore.uploadFile(formDataPart1)

      // Generate PDF for Part 2 using html2canvas
      const filePart2 = await renderElementToPdfFile(
        'divToPrintPart2',
        `${currentUser?.code}_${ts}_part2`
      )
      const formDataPart2 = new FormData()
      formDataPart2.append('file', filePart2)
      const responseSignDocPart2 = await fileStore.uploadFile(formDataPart2)

      // Merge the two PDFs
      const pdfFilePrefix = isDelegateTab ? 'PLYK-NĐD' : 'PLYK'
      const pdfDocumentNumber = toDisplayDocumentNumber(
        feedbackDetailData?.documentNumber
      )
      const pdfFileName1 = pdfDocumentNumber
        ? `${pdfFilePrefix}_${pdfDocumentNumber}_${(
            currentUser?.name_uppercase || ''
          ).replace(/\s+/g, '_')}`
        : `Bieu_quyet_${new Date().getTime()}`
      const mergedResult = await PdfEsignUltil.mergeCurrentPdfWithGenerated(
        responseSignDocPart1.data.file_id,
        responseSignDocPart2.data.file_id,
        pdfFileName1
      )

      // Refresh để tìm record cần update - luôn dùng targetCommentId (selectedIndex)
      await FeedbackCommentStore.getFeedbackCommentById(targetCommentId)
      const freshDetailsForVote =
        FeedbackCommentStore.feedbackCommentDataDetailV2
          ?.feedbackCommentDetails || []

      const latestFeedbackForCurrentUser = freshDetailsForVote
        .filter(
          item =>
            item.feedbackUsername === currentUser?.username &&
            item.voteType &&
            !item.isDeleted
        )
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )[0]

      const dataSubmit = {
        feedbackUsername: currentUser?.username,
        detailFeedbackComment: newComment,
        voteType: currentSelectedOption,
        fileId: mergedResult.file_id,
        fileName: mergedResult.file_name,
        uploadFileId: uploadFileIds,
        uploadFileName: uploadFileNames.join(', '),
      }

      if (latestFeedbackForCurrentUser) {
        const hasNewComment = newComment && newComment.trim() !== ''

        if (hasNewComment) {
          await FeedbackCommentStore.updateVotingStatus(
            latestFeedbackForCurrentUser?.id,
            dataSubmit
          )
        } else {
          await FeedbackCommentStore.updateVotingStatus(
            latestFeedbackForCurrentUser?.id,
            {
              ...dataSubmit,
              detailFeedbackComment:
                latestFeedbackForCurrentUser.detailFeedbackComment,
            }
          )
        }
      } else {
        await FeedbackCommentStore.updateComment(
          dataSubmit,
          targetCommentId  // dùng targetCommentId thay vì feedbackCommentDataDetailV2?.id
        )
      }

      // Luôn dùng targetCommentId để refresh đúng content đang chọn
      await FeedbackCommentStore.getFeedbackCommentById(targetCommentId)

      message.success('Thêm biểu quyết thành công!')

      // Wait 1.5s for backend indices/DB replication to fully commit before refetching
      await new Promise(resolve => setTimeout(resolve, 1500))

      await fetchFeedbackDetailData()
      // FIX: dùng targetCommentId (selectedIndex) thay vì feedbackCommentDataDetailV2.id
      // để đảm bảo feedbackCommentDataDetailV2 luôn trỏ đúng content đang được chọn
      await fetchDetailComment(targetCommentId)

      // Clear editor and uploaded files after successful vote submit
      if (EDITOR_REF.current?.editor) {
        EDITOR_REF.current.editor.setData('')
      }
      setUploadedFiles([])
    } catch (err) {
      console.error('Error in voting:', err)
      message.error('Có lỗi xảy ra khi lưu biểu quyết!')
    } finally {
      setIsVoting(false)
    }
  }

  useEffect(() => {
    ;(async () => {
      try {
        await selectPeopleStore.getGroupList()
        await selectPeopleStore.getDepartmentWithUsersV2()
        await selectPeopleStore.getDepartmentWithUsers('CPN7451091748209')
      } catch (err) {
        console.log(err)
        message.error(err.vi || 'Đã có lỗi xảy ra43456!')
        loadingAnimationStore.showSpinner(false)
      }
    })()
  }, [])

  feedbackDetailData?.assignees?.forEach(assignee => {
    const matchingData = departmentWithUserListv2?.find(
      item => item.code === assignee.assignee_code
    )
    if (matchingData) {
      assignee.length = matchingData.length
    }
  })
  const departmentMap = useMemo(() => {
    const map = new Map()
    departmentWithUserListv2?.forEach(dept => {
      map.set(dept.code, dept.name)
      dept.users.forEach(user => {
        map.set(user.user_code, user.name_uppercase)
      })
    })
    return map
  }, [departmentWithUserListv2])

  useEffect(() => {
    if (!feedbackDetailData?.assignees || !departmentMap.size) return

    const assigneesProcessed = feedbackDetailData.assignees.map(assignee => {
      assignee.name_uppercase =
        departmentMap.get(assignee.assignee_code) || assignee.name_uppercase
      return assignee
    })

    setAsigneeData(assigneesProcessed)
    loadingAnimationStore.showSpinner(false)
  }, [departmentMap, feedbackDetailData?.assignees])

  const handleOpenEditModal = () => {
    console.log('Opening edit modal')
    setIsEditModalVisible(true)
  }

  const handleCloseEditModal = () => {
    console.log('Closing edit modal')
    setIsEditModalVisible(false)
  }

  const handleOpenAddPersonModal = () => {
    setIsAddPersonModalVisible(true)
  }

  const handleCloseAddPersonModal = () => {
    setIsAddPersonModalVisible(false)
  }

  const handleExportDocx = shouldExport => {
    if (shouldExport) {
      setExportDocx(true)
    }
  }

  useEffect(() => {
    if (exportDocx) {
      setExportDocx(false)
    }
  }, [exportDocx])

  const fetchFeedbackDetailData = async () => {
    try {
      loadingAnimationStore.showSpinner(true)
      await FeedbackProcedureStore.getElectronicDocumentById(feedbackId)
    } catch (err) {
      console.log(err)
    } finally {
      loadingAnimationStore.showSpinner(false)
    }
  }

  const idIncludes = useMemo(() => {
    return feedbackDetailData?.feedbackComments?.map(item => item.id) || []
  }, [feedbackDetailData])

  useEffect(() => {
    fetchFeedbackDetailData()
  }, [feedbackId])

  // Auto-select first item when data is loaded, and ensure current selection is valid for this data
  useEffect(() => {
    if (feedbackDetailData?.feedbackComments?.length > 0) {
      if (selectedIndex === null || !idIncludes.includes(selectedIndex)) {
        handleItemClick(0, feedbackDetailData?.feedbackComments[0]?.id)
      }
    }
  }, [feedbackDetailData, feedbackId, selectedIndex, idIncludes])

  // Build username→userDetail map to access position, names, etc.
  const usernameToUserDetailMap = useMemo(() => {
    const map = new Map()
    departmentWithUserListv2?.forEach(dept => {
      dept.users?.forEach(user => {
        if (user.user_name) {
          map.set(user.user_name, user)
        }
      })
    })
    return map
  }, [departmentWithUserListv2])

  const isFeedbackHdqt = location.pathname.includes('feedback-hdqt')

  const handlerUsernames = useMemo(() => {
    const picAssignees =
      feedbackDetailData?.assignees?.filter(
        assignee => assignee.permission === 'PIC'
      ) || []

    const usernames = new Set()

    picAssignees.forEach(assignee => {
      if (assignee.assignee_type === 'USER') {
        usernames.add(assignee.assignee_code)
      } else if (assignee.assignee_type === 'DPT') {
        const department = departmentWithUserListv2?.find(
          dept => dept.code === assignee.assignee_code
        )
        department?.users?.forEach(user => {
          usernames.add(user.user_name)
        })
      }
    })

    return usernames
  }, [feedbackDetailData?.assignees, departmentWithUserListv2])

  const exportUsernames = useMemo(() => {
    const usernames = new Set(handlerUsernames)

    ;(feedbackDetailData?.feedbackComments || []).forEach(comment => {
      ;(comment?.feedbackCommentDetails || []).forEach(detail => {
        if (detail?.feedbackUsername) {
          usernames.add(detail.feedbackUsername)
        }
      })
    })

    return Array.from(usernames).filter(Boolean)
  }, [handlerUsernames, feedbackDetailData?.feedbackComments])

  useEffect(() => {
    const missingUsernames = exportUsernames.filter(username => {
      return (
        !usernameToUserDetailMap.has(username) &&
        !fetchedUserDetails[username.toLowerCase()]
      )
    })

    if (missingUsernames.length === 0) return

    let cancelled = false

    ;(async () => {
      try {
        const responses = await Promise.all(
          missingUsernames.map(username =>
            userStore
              .getUserByUsername(username)
              .then(response => ({ username, data: response?.data }))
              .catch(() => null)
          )
        )

        if (cancelled) return

        setFetchedUserDetails(prev => {
          const next = { ...prev }

          responses.forEach(result => {
            if (!result?.data) return
            const userDetail = result.data
            const resolvedUsername =
              userDetail?.user_name || userDetail?.username || result.username

            if (resolvedUsername) {
              next[resolvedUsername.toLowerCase()] = userDetail
            }
          })

          return next
        })
      } catch (error) {
        console.error('Failed to fetch user details for export', error)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [exportUsernames, usernameToUserDetailMap, fetchedUserDetails, userStore])

  const resolvedUserDetailMap = useMemo(() => {
    const map = new Map(usernameToUserDetailMap)

    Object.values(fetchedUserDetails).forEach(user => {
      const username = user?.user_name || user?.username
      if (username) {
        map.set(username, user)
      }
    })

    return map
  }, [usernameToUserDetailMap, fetchedUserDetails])

  console.log(JSON.stringify(resolvedUserDetailMap))

  const votingAssigneeOrderMap = useMemo(() => {
    const map = new Map()
    const picAssignees =
      feedbackDetailData?.assignees?.filter(
        assignee => assignee.permission === 'PIC'
      ) || []
    let order = 0

    picAssignees.forEach(assignee => {
      if (assignee.assignee_type === 'USER') {
        if (!map.has(assignee.assignee_code)) {
          map.set(assignee.assignee_code, order)
          order += 1
        }
        return
      }

      if (assignee.assignee_type === 'DPT') {
        const department = departmentWithUserListv2?.find(
          dept => dept.code === assignee.assignee_code
        )

        department?.users?.forEach(user => {
          if (user?.user_name && !map.has(user.user_name)) {
            map.set(user.user_name, order)
            order += 1
          }
        })
      }
    })

    return map
  }, [feedbackDetailData?.assignees, departmentWithUserListv2])

  // FIX: thêm !comment.isDeleted để loại bỏ record bị xóa mềm khỏi tính tỉ lệ
  const latestFeedbacks =
    feedbackCommentDataDetailV2?.feedbackCommentDetails &&
    Array.from(
      feedbackCommentDataDetailV2.feedbackCommentDetails
        .reduce((map, comment) => {
          if (comment.fileId && !comment.isDeleted) {
            const existing = map.get(comment.feedbackUsername)
            if (
              !existing ||
              new Date(comment.createdAt) > new Date(existing.createdAt)
            ) {
              map.set(comment.feedbackUsername, comment)
            }
          }
          return map
        }, new Map())
        .values()
    )

  const filteredLatestFeedbacks = useMemo(() => {
    const feedbacks =
      latestFeedbacks?.filter(feedback =>
        handlerUsernames.has(feedback.feedbackUsername)
      ) || []

    return [...feedbacks].sort((a, b) => {
      const userA = resolvedUserDetailMap.get(a.feedbackUsername)
      const userB = resolvedUserDetailMap.get(b.feedbackUsername)

      if (isFeedbackHdqt) {
        const hdqtOrderA = getHdqtVotingOrder(
          a.feedbackUsername,
          userA?.name_uppercase
        )
        const hdqtOrderB = getHdqtVotingOrder(
          b.feedbackUsername,
          userB?.name_uppercase
        )

        if (hdqtOrderA !== null || hdqtOrderB !== null) {
          return (
            (hdqtOrderA ?? Number.MAX_SAFE_INTEGER) -
            (hdqtOrderB ?? Number.MAX_SAFE_INTEGER)
          )
        }
      }

      const priorityA = getVotingPositionPriority(userA)
      const priorityB = getVotingPositionPriority(userB)

      if (priorityA !== priorityB) {
        return priorityA - priorityB
      }

      if (priorityA !== 99 && priorityB !== 99) {
        const displayNameCompare = compareVietnameseName(
          userA?.name_uppercase,
          userB?.name_uppercase
        )
        if (displayNameCompare !== 0) {
          return displayNameCompare
        }
      }

      const assigneeOrderA =
        votingAssigneeOrderMap.get(a.feedbackUsername) ??
        Number.MAX_SAFE_INTEGER
      const assigneeOrderB =
        votingAssigneeOrderMap.get(b.feedbackUsername) ??
        Number.MAX_SAFE_INTEGER

      if (assigneeOrderA !== assigneeOrderB) {
        return assigneeOrderA - assigneeOrderB
      }

      // Fallback to name if positions are same priority
      return (userA?.name_uppercase || '').localeCompare(
        userB?.name_uppercase || ''
      )
    })
  }, [
    latestFeedbacks,
    handlerUsernames,
    resolvedUserDetailMap,
    votingAssigneeOrderMap,
    isFeedbackHdqt,
  ])

  const filteredLatestFeedbacksAllContents = useMemo(() => {
    const allDetails = (feedbackDetailData?.feedbackComments || []).flatMap(
      comment => comment.feedbackCommentDetails || []
    )

    // Merge in fresh data from the currently viewed comment detail
    const freshDetails =
      feedbackCommentDataDetailV2?.feedbackCommentDetails || []
    const mergedDetails = [...allDetails, ...freshDetails]

    const latestByUser = Array.from(
      mergedDetails
        .reduce((map, detail) => {
          if (!detail.fileId || detail.isDeleted) return map

          const existing = map.get(detail.feedbackUsername)
          if (
            !existing ||
            new Date(detail.updatedAt) > new Date(existing.updatedAt)
          ) {
            map.set(detail.feedbackUsername, detail)
          }
          return map
        }, new Map())
        .values()
    )

    const feedbacks =
      latestByUser?.filter(feedback =>
        handlerUsernames.has(feedback.feedbackUsername)
      ) || []

    return [...feedbacks].sort((a, b) => {
      const userA = resolvedUserDetailMap.get(a.feedbackUsername)
      const userB = resolvedUserDetailMap.get(b.feedbackUsername)

      if (isFeedbackHdqt) {
        const hdqtOrderA = getHdqtVotingOrder(
          a.feedbackUsername,
          userA?.name_uppercase
        )
        const hdqtOrderB = getHdqtVotingOrder(
          b.feedbackUsername,
          userB?.name_uppercase
        )

        if (hdqtOrderA !== null || hdqtOrderB !== null) {
          return (
            (hdqtOrderA ?? Number.MAX_SAFE_INTEGER) -
            (hdqtOrderB ?? Number.MAX_SAFE_INTEGER)
          )
        }
      }

      const priorityA = getVotingPositionPriority(userA)
      const priorityB = getVotingPositionPriority(userB)

      if (priorityA !== priorityB) {
        return priorityA - priorityB
      }

      if (priorityA !== 99 && priorityB !== 99) {
        const displayNameCompare = compareVietnameseName(
          userA?.name_uppercase,
          userB?.name_uppercase
        )
        if (displayNameCompare !== 0) {
          return displayNameCompare
        }
      }

      const assigneeOrderA =
        votingAssigneeOrderMap.get(a.feedbackUsername) ??
        Number.MAX_SAFE_INTEGER
      const assigneeOrderB =
        votingAssigneeOrderMap.get(b.feedbackUsername) ??
        Number.MAX_SAFE_INTEGER

      if (assigneeOrderA !== assigneeOrderB) {
        return assigneeOrderA - assigneeOrderB
      }

      return (userA?.name_uppercase || '').localeCompare(
        userB?.name_uppercase || ''
      )
    })
  }, [
    feedbackDetailData?.feedbackComments,
    feedbackCommentDataDetailV2?.feedbackCommentDetails,
    handlerUsernames,
    resolvedUserDetailMap,
    votingAssigneeOrderMap,
    isFeedbackHdqt,
  ])

  function getNameUppercaseByUsername(username) {
    const user = resolvedUserDetailMap.get(username)
    if (user && user.name_uppercase) {
      return utils.getNameInCapitalize(user.name_uppercase)
    }
    return 'Admin'
  }

  const [allFileNames, setAllFileNames] = useState([])
  useEffect(() => {
    const fetchFileNames = async () => {
      if (!feedbackDetailData?.fileIdAttachment) return
      const allFileNamesResponse = await Promise.all(
        feedbackDetailData.fileIdAttachment.map(fileId =>
          FeedbackProcedureStore.getElectronicDocumentFileName(
            fileId.fileId.replace(/[\[\]]/g, '')
          )
        )
      )
      setAllFileNames(allFileNamesResponse.map(response => response.data))
    }
    fetchFileNames()
  }, [feedbackDetailData?.fileIdAttachment])

  const feedbackComments = feedbackCommentDataDetailV2?.feedbackCommentDetails

  const results = feedbackDetailData?.feedbackComments?.map(comment => {
    const latestVotes = {}
    comment.feedbackCommentDetails.forEach(detail => {
      const { feedbackUsername, voteType, createdAt } = detail
      if (voteType) {
        if (
          !latestVotes[feedbackUsername] ||
          new Date(latestVotes[feedbackUsername].createdAt) <
            new Date(createdAt)
        ) {
          latestVotes[feedbackUsername] = {
            ...detail,
            content: comment.content,
          }
        }
      }
    })
    return Object.values(latestVotes)
  })

  const voteCounts = {
    AGREE: 0,
    DISAGREE: 0,
    OTHER: 0,
  }

  let totalCount = 0

  results?.forEach(feedbackGroup => {
    feedbackGroup.forEach(feedback => {
      if (feedback.voteType in voteCounts) {
        voteCounts[feedback.voteType]++
      }
      totalCount++
    })
  })

  const countAllUser = latestFeedbacks?.length || 0
  const countAgree =
    filteredLatestFeedbacks?.filter(comment => comment.voteType === 'AGREE')
      ?.length || 0

  const countDisAgree =
    filteredLatestFeedbacks?.filter(comment => comment.voteType === 'DISAGREE')
      ?.length || 0

  const countOther =
    filteredLatestFeedbacks?.filter(comment => comment.voteType === 'OTHER')
      ?.length || 0

  let agreeCount = 0
  let disagreeCount = 0
  let otherCount = 0
  latestFeedbacks?.forEach(comment => {
    comment?.feedbackCommentDetails?.forEach(detail => {
      if (detail.voteType === 'AGREE') {
        agreeCount++
      } else if (detail.voteType === 'DISAGREE') {
        disagreeCount++
      } else if (detail.voteType === 'OTHER') {
        otherCount++
      }
    })
  })
  let totalVote = agreeCount + disagreeCount + otherCount

  const picAssignees =
    feedbackDetailData?.assignees?.filter(
      assignee => assignee.permission === 'PIC'
    ) || []

  const uniqueAssignees = []
  const seenCodes = new Set()

  for (const assignee of picAssignees) {
    if (!seenCodes.has(assignee.assignee_code)) {
      uniqueAssignees.push(assignee)
      seenCodes.add(assignee.assignee_code)
    } else {
      const existingIndex = uniqueAssignees.findIndex(
        a => a.assignee_code === assignee.assignee_code
      )
      if (
        existingIndex !== -1 &&
        uniqueAssignees[existingIndex].assignee_type === 'USER' &&
        assignee.assignee_type === 'DPT'
      ) {
        uniqueAssignees[existingIndex] = assignee
      }
    }
  }

  const totalPeopleCount = uniqueAssignees.reduce(
    (sum, assignee) => sum + (assignee.length || 0),
    0
  )

  const countedUsers = new Set()

  departmentWithUserListv2?.forEach(department => {
    department.users.forEach(user => {
      countedUsers.add(user.user_code)
    })
  })

  let totalPeopleCountv2 = 0

  picAssignees.forEach(individual => {
    if (individual.assignee_type === 'USER') {
      if (!countedUsers.has(individual.assignee_code)) {
        totalPeopleCountv2 += 1
      }
    } else if (individual.assignee_type === 'DPT') {
      if (!countedUsers.has(individual.assignee_code)) {
        totalPeopleCountv2 += individual.length
        countedUsers.add(individual.assignee_code)
      }
    }
  })

  const handleDelete = () => {
    Modal.confirm({
      title: 'Bạn có muốn xóa ý kiến này không?',
      okText: 'Đồng ý',
      okType: 'danger',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          await FeedbackProcedureRequest.deleteFeedbackProcedure(
            feedbackDetailData.id
          )
          message.success('Đã xóa ý kiến thành công!')
          history.push({
            pathname: '/feedback-hdqt',
            state: {
              ...(location?.state || {}),
              feedbackTab: currentFeedbackTab,
            },
          })
        } catch (error) {
          // console.log('Error when delete feedback procedure', error)
        }
      },
      onCancel() {
        console.log('Cancelled deletion')
      },
    })
  }

  feedbackDetailData?.assignees?.forEach(el => {
    const user = resolvedUserDetailMap.get(el.assignee_code)
    if (user && user.name_uppercase) {
      el.name_uppercase = user.name_uppercase
    }
  })

  const handlerAssignList = feedbackDetailData?.assignees
    ?.filter(el => el.permission === 'PIC')
    ?.reduce((unique, item) => {
      const isDuplicate = unique.some(
        el => el.assignee_code === item.assignee_code
      )
      return isDuplicate ? unique : [...unique, item]
    }, [])

  const viewerAssignList = feedbackDetailData?.assignees
    ?.filter(el => el.permission === 'COOR')
    ?.reduce((unique, item) => {
      const isDuplicate = unique.some(
        el => el.assignee_code === item.assignee_code
      )
      return isDuplicate ? unique : [...unique, item]
    }, [])

  const combinerAssignList = feedbackDetailData?.assignees
    ?.filter(el => el.permission === 'VIEW')
    ?.reduce((unique, item) => {
      const isDuplicate = unique.some(
        el => el.assignee_code === item.assignee_code
      )
      return isDuplicate ? unique : [...unique, item]
    }, [])

  const isVotingUser = feedbackDetailData?.assignees?.filter(
    el => el.permission === 'PIC'
  )
  const isUnVotingUser = feedbackDetailData?.assignees?.filter(
    el => el.permission === 'VIEW'
  )
  const userFromDepartment = (departmentWithUserListv2 ?? [])
    .filter(
      group =>
        Array.isArray(isVotingUser) &&
        isVotingUser.some(assignee => assignee.assignee_code === group.code)
    )
    .flatMap(group => group.users ?? [])

  const userFromUserName = (departmentWithUserListv2 ?? []).flatMap(group => {
    const users = Array.isArray(group.users) ? group.users : []

    return users.filter(
      user =>
        Array.isArray(isVotingUser) &&
        isVotingUser.some(assignee => assignee.assignee_code === user.user_name)
    )
  })

  const uniqueUsers = [...userFromDepartment, ...userFromUserName]
    .reduce((acc, user) => {
      if (
        !acc.some(existingUser => existingUser.user_name === user.user_name)
      ) {
        acc.push(user)
      }
      return acc
    }, [])
    .sort((a, b) => {
      if (isFeedbackHdqt) {
        const hdqtOrderA = getHdqtVotingOrder(a.user_name, a?.name_uppercase)
        const hdqtOrderB = getHdqtVotingOrder(b.user_name, b?.name_uppercase)

        if (hdqtOrderA !== null || hdqtOrderB !== null) {
          return (
            (hdqtOrderA ?? Number.MAX_SAFE_INTEGER) -
            (hdqtOrderB ?? Number.MAX_SAFE_INTEGER)
          )
        }
      }

      const priorityA = getVotingPositionPriority(a)
      const priorityB = getVotingPositionPriority(b)
      if (priorityA !== priorityB) return priorityA - priorityB

      if (priorityA !== 99 && priorityB !== 99) {
        const displayNameCompare = compareVietnameseName(
          a?.name_uppercase,
          b?.name_uppercase
        )
        if (displayNameCompare !== 0) return displayNameCompare
      }

      const assigneeOrderA =
        votingAssigneeOrderMap.get(a.user_name) ?? Number.MAX_SAFE_INTEGER
      const assigneeOrderB =
        votingAssigneeOrderMap.get(b.user_name) ?? Number.MAX_SAFE_INTEGER
      if (assigneeOrderA !== assigneeOrderB)
        return assigneeOrderA - assigneeOrderB

      return (a.name_uppercase || '').localeCompare(b.name_uppercase || '')
    })

  const exportUserList = useMemo(() => {
    const allUsers = [
      ...uniqueUsers,
      ...Array.from(resolvedUserDetailMap.values()),
    ]

    return allUsers.reduce((acc, user) => {
      const username = user?.user_name || user?.username
      if (!username) return acc

      const existed = acc.some(
        item => (item?.user_name || item?.username) === username
      )
      if (!existed) acc.push(user)

      return acc
    }, [])
  }, [uniqueUsers, resolvedUserDetailMap])

  const exportPositionByUsername = useMemo(() => {
    const addUserPosition = (acc, user) => {
      const username = (user?.user_name || user?.username || '').trim()
      const positionName = getUserPositionName(user)
      if (username && positionName) {
        acc[username.toLowerCase()] = positionName
      }
    }

    const map = {}

    // Load full position names from /v1/departments/users API
    departmentWithUserList?.forEach(dept => {
      dept?.users?.forEach(user => {
        addUserPosition(map, user)
      })
    })

    Array.from(resolvedUserDetailMap.values()).forEach(user =>
      addUserPosition(map, user)
    )

    uniqueUsers.forEach(user => addUserPosition(map, user))

    const usersFromStore = userStore?.userList || []
    usersFromStore.forEach(user => addUserPosition(map, user))

    filteredLatestFeedbacksAllContents.forEach(detail => {
      const username = (detail?.feedbackUsername || '').trim().toLowerCase()
      const positionName =
        detail?.position || detail?.positionName || detail?.position_name || ''
      if (username && positionName) {
        map[username] = positionName
      }
    })

    addUserPosition(map, currentUser)

    return map
  }, [
    departmentWithUserList,
    resolvedUserDetailMap,
    uniqueUsers,
    userStore?.userList,
    filteredLatestFeedbacksAllContents,
    currentUser,
  ])

  const userNameHaventVoting = uniqueUsers.map(user => user.user_name)

  const isCreator = feedbackDetailData?.assignees?.some(
    a => a.permission === 'CREATE' && a.assignee_code === currentUser?.username
  )

  // Quá hạn khi thời điểm hiện tại đã vượt qua endDate của phiếu
  const isOverdue = useMemo(() => {
    if (!feedbackDetailData?.endDate) return false

    const endDate = new Date(feedbackDetailData.endDate)
    if (isNaN(endDate)) return false

    return new Date() > endDate
  }, [feedbackDetailData?.endDate])

  const canEdit = isCreator

  const menuHandleDocument = (
    <Menu>
      {canEdit && (
        <Menu.Item
          icon={<FormOutlined style={{ color: blue }} />}
          onClick={handleOpenEditModal}>
          <span style={{ color: blue }}>Chỉnh sửa</span>
        </Menu.Item>
      )}
      {aclStore.checkAccessControlAction(ACL_ACTION_TYPE.add_people__HDQT) && (
        <Menu.Item
          icon={<UserAddOutlined style={{ color: blue }} />}
          onClick={handleOpenAddPersonModal}>
          <span style={{ color: blue }}>Thêm người</span>
        </Menu.Item>
      )}
      {aclStore.checkAccessControlAction(ACL_ACTION_TYPE.export__HDQT) && (
        <Menu.Item
          icon={<PrinterOutlined style={{ color: blue }} />}
          onClick={() => {
            handleExportDocx(true)
          }}>
          <span style={{ color: blue }}>In</span>
        </Menu.Item>
      )}

      {aclStore.checkAccessControlAction(ACL_ACTION_TYPE.delete__HDQT) &&
        canEdit && (
          <Menu.Item danger onClick={handleDelete} icon={<DeleteOutlined />}>
            Xóa phiếu
          </Menu.Item>
        )}
    </Menu>
  )

  // FIX: dùng feedbackCommentDataDetailV2 (có isDeleted) thay vì feedbackDetailData (không có isDeleted)
  const getVoteCountsForFeedback = useCallback(() => {
    const voteCounts = { AGREE: 0, DISAGREE: 0, OTHER: 0 }
    let totalCount = 0

    const details = feedbackCommentDataDetailV2?.feedbackCommentDetails || []

    const latestVotes = details
      .filter(
        detail =>
          handlerUsernames.has(detail.feedbackUsername) &&
          detail.voteType &&
          detail.fileId &&
          !detail.isDeleted
      )
      .reduce((acc, detail) => {
        const { feedbackUsername, createdAt } = detail
        if (
          !acc[feedbackUsername] ||
          new Date(acc[feedbackUsername].createdAt) < new Date(createdAt)
        ) {
          acc[feedbackUsername] = detail
        }
        return acc
      }, {})

    Object.values(latestVotes).forEach(detail => {
      if (detail.voteType in voteCounts) {
        voteCounts[detail.voteType]++
        totalCount++
      }
    })

    return { voteCounts, totalCount }
  }, [feedbackCommentDataDetailV2?.feedbackCommentDetails, handlerUsernames])

  const formatDateTime = dateTimeString => {
    const date = new Date(dateTimeString)

    if (isNaN(date)) {
      return 'Invalid date'
    }

    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    const seconds = String(date.getSeconds()).padStart(2, '0')

    return `${day}/${month}/${year} - ${hours}:${minutes}:${seconds}`
  }

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen)
  }

  const handleItemClick = (index, fId) => {
    fetchDetailComment(fId)
    setSelectedIndex(fId)
    setIndexCore(index)
  }

  function htmlToText(html) {
    const tempElement = document.createElement('div')
    tempElement.innerHTML = html
    return tempElement.textContent || tempElement.innerText || ''
  }

  const handleOpenPopupSign = () => {
    if (!fileIdWithCurrentUser) {
      return message.info('Bạn chưa biểu quyết')
    }
    signedDocumentStore.setFileBlob(documentAttachment)
    signedDocumentStore.setOriginFileObj({
      name: latestFeedbacks?.fileName,
    })
    setIsVisiblePopupSign(true)
  }

  const feedbackWithCurrentUser = filteredLatestFeedbacks?.find(
    item => item?.feedbackUsername === currentUser?.username
  )

  const {
    fileId: fileIdWithCurrentUser,
    fileName: fileNameWithCurrentUser,
    detailFeedbackComment: commentWithCurrentUser,
    voteType: voteTypeWithCurrentUser,
    id: latestId,
  } = feedbackWithCurrentUser || {}

  const handleAfterSign = async fileSignId => {
    if (!fileSignId) return
    try {
      setIsVisiblePopupSign(false)
      const updateStatusData = {
        feedbackUsername: currentUser?.username,
        detailFeedbackComment: commentWithCurrentUser,
        voteType: selectedOption,
        fileId: fileSignId.fileSignUrl,
        fileName: fileSignId.fileOriginName,
      }
      await FeedbackCommentStore.updateVotingStatus(latestId, updateStatusData)
      await fetchDetailComment(feedbackCommentDataDetailV2.id)
    } catch (error) {
      message.error(error.message)
    } finally {
      setIsVisiblePopupSign(false)
    }
  }

  const currentUserFeedbacks =
    feedbackCommentDataDetailV2?.feedbackCommentDetails?.filter(
      item => item.feedbackUsername === currentUser?.username && !item.isDeleted
    ) || []

  const sortedFeedbacks = currentUserFeedbacks.sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
  )

  const displayComment = sortedFeedbacks
    .filter(
      feedback =>
        feedback.detailFeedbackComment &&
        feedback.detailFeedbackComment.trim() !== ''
    )
    .map(feedback => feedback.detailFeedbackComment)
    .join('<br/>')

  // Tính toán số phiếu và phần trăm cho mỗi lựa chọn
  const { voteCounts: currentVoteCounts } = getVoteCountsForFeedback()

  const effectiveVoteCounts = { ...currentVoteCounts }
  if (feedbackDetailData?.isTTr) {
    effectiveVoteCounts.AGREE = Math.min(
      totalPeopleCountv2,
      effectiveVoteCounts.AGREE + 1
    )
  }

  const totalPeopleBase = totalPeopleCountv2 || 1
  const voteOptions = [
    {
      value: 'AGREE',
      label: 'Tán thành',
      count: effectiveVoteCounts.AGREE,
      percent: ((effectiveVoteCounts.AGREE / totalPeopleBase) * 100).toFixed(0),
    },
    {
      value: 'DISAGREE',
      label: 'Không tán thành',
      count: effectiveVoteCounts.DISAGREE,
      percent: ((effectiveVoteCounts.DISAGREE / totalPeopleBase) * 100).toFixed(
        0
      ),
    },
    ...(!isDelegateTab
      ? [
          {
            value: 'OTHER',
            label: 'Không có ý kiến',
            count: effectiveVoteCounts.OTHER,
            percent: (
              (effectiveVoteCounts.OTHER / totalPeopleBase) *
              100
            ).toFixed(0),
          },
        ]
      : []),
  ]

  return (
    <>
      <DashboardLayout>
        <Helmet>
          <title>Chi tiết Phiếu lấy ý kiến | VIMC Quản lý văn bản VIMC</title>
        </Helmet>
        <AppPageHeader
          title={
            <>
              {feedbackDetailData.title}
              {feedbackDetailData.documentNumber && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 14,
                  }}>
                  <span style={{ fontWeight: 700 }}>Số hồ sơ:</span>
                  <span style={{ fontWeight: 500 }}>
                    {toDisplayDocumentNumber(feedbackDetailData.documentNumber)}
                    /{plykSuffix}
                  </span>
                </div>
              )}
            </>
          }
          showBackButton
          addon={
            <Dropdown overlay={menuHandleDocument} trigger={['click']}>
              <Button
                style={{ width: 40, height: 40, flexShrink: 0 }}
                icon={
                  <MenuDotsBoldIcon
                    style={{
                      width: 20,
                      height: 20,
                      transform: 'rotate(90deg)',
                    }}
                  />
                }
                className="custom-ant-button icon-btn"
                type="default"
              />
            </Dropdown>
          }
        />
        {/* <PageTitle
          location={props.location}
          title={feedbackDetailData.title}
          showTitle={true}>
          <HeadingButtonWrapper>
            <Dropdown overlay={menuHandleDocument} trigger={['click']}>
              <span className={'btn-style'}>
                <MoreOutlined style={{ color: blue }} />
              </span>
            </Dropdown>
          </HeadingButtonWrapper>
        </PageTitle> */}
        <>
          <div>
            <ContentWrapper>
              <BlockLeft>
                {feedbackDetailData.legalBasis && (
                  <ContentBlockWrapper style={{ marginBottom: 20 }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontWeight: 500,
                        fontSize: 14,
                        borderTopLeftRadius: 8,
                        borderTopRightRadius: 8,
                        padding: '10px 16px',
                        color: '#fff',
                        backgroundColor: blue,
                        cursor: 'pointer',
                      }}
                      onClick={() => setIsLegalBasisOpen(!isLegalBasisOpen)}>
                      <span>Căn cứ</span>
                      {isLegalBasisOpen ? (
                        <CaretDownFilled />
                      ) : (
                        <CaretLeftFilled />
                      )}
                    </div>
                    <div
                      style={{
                        padding: '16px',
                      }}>
                      <div
                        style={{
                          overflow: 'hidden',
                          maxHeight: isLegalBasisOpen ? '1000px' : '0',
                          opacity: isLegalBasisOpen ? 1 : 0,
                          transition:
                            'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease',
                        }}>
                        <div
                          style={{
                            maxWidth: '1200px',
                            whiteSpace: 'pre-wrap',
                            overflowWrap: 'break-word',
                          }}
                          dangerouslySetInnerHTML={{
                            __html: feedbackDetailData.legalBasis,
                          }}></div>
                      </div>
                    </div>
                  </ContentBlockWrapper>
                )}

                {feedbackDetailData.purpose && (
                  <ContentBlockWrapper style={{ marginBottom: 20 }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontWeight: 500,
                        fontSize: 14,
                        borderTopLeftRadius: 8,
                        borderTopRightRadius: 8,
                        padding: '10px 16px',
                        color: '#fff',
                        backgroundColor: blue,
                        cursor: 'pointer',
                      }}
                      onClick={() => setIsPurposeOpen(!isPurposeOpen)}>
                      <span>Mục đích</span>
                      {isPurposeOpen ? (
                        <CaretDownFilled />
                      ) : (
                        <CaretLeftFilled />
                      )}
                    </div>
                    <div
                      style={{
                        padding: '16px',
                      }}>
                      <div
                        style={{
                          overflow: 'hidden',
                          maxHeight: isPurposeOpen ? '1000px' : '0',
                          opacity: isPurposeOpen ? 1 : 0,
                          transition:
                            'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease',
                        }}>
                        <div
                          style={{
                            maxWidth: '1200px',
                            whiteSpace: 'pre-wrap',
                            overflowWrap: 'break-word',
                          }}
                          dangerouslySetInnerHTML={{
                            __html: feedbackDetailData.purpose,
                          }}></div>
                      </div>
                    </div>
                  </ContentBlockWrapper>
                )}
                <ContentBlockWrapper
                  style={{
                    marginBottom: 20,
                  }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontWeight: 500,
                      fontSize: 14,
                      borderTopLeftRadius: 8,
                      borderTopRightRadius: 8,
                      padding: '10px 16px',
                      color: '#fff',
                      backgroundColor: blue,
                      cursor: 'pointer',
                    }}
                    onClick={toggleMenu}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span>Nội dung ý kiến</span>
                      <p style={{ fontSize: 10, margin: 0 }}>
                        (Chọn ý kiến bên dưới để xem chi tiết)
                      </p>
                    </div>
                    {isMenuOpen ? <CaretDownFilled /> : <CaretLeftFilled />}
                  </div>
                  <div
                    style={{
                      padding: '16px',
                      overflowX: 'hidden',
                      overflowY: isMenuOpen ? 'auto' : 'hidden',
                      maxHeight: isMenuOpen ? '560px' : '0',
                      opacity: isMenuOpen ? 1 : 0,
                      transition:
                        'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease',
                      backgroundColor: '#fff',
                    }}>
                    {' '}
                    <Menu
                      mode="inline"
                      style={{
                        width: '100%',
                        border: 'none',
                        boxSizing: 'border-box',
                      }}>
                      {feedbackDetailData?.feedbackComments?.map(
                        (feedback, index) => {
                          const feedbackTitle = htmlToText(feedback.content)
                            .replace(/\s+/g, ' ')
                            .trim()

                          return (
                            <Menu.Item
                              key={index}
                              style={{
                                fontSize: '12px',
                                fontWeight: '500',
                                borderRadius: 6,
                                margin: indexCore === index ? '5px' : 0,
                                backgroundColor:
                                  indexCore === index ? '#E6F4FF' : '#fff',
                                color: indexCore === index ? '#1677FF' : '#000',
                                padding: '8px 12px',
                                height: 'auto',
                                lineHeight: 'normal',
                              }}
                              onClick={() =>
                                handleItemClick(index, feedback.id)
                              }>
                              <div
                                // title={feedbackTitle || `Ý kiến ${index + 1}`}
                                placement="top"
                                overlayStyle={{
                                  maxWidth: '380px',
                                  whiteSpace: 'normal',
                                }}>
                                <div
                                  style={{
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    lineHeight: '1.5',
                                  }}>
                                  <span
                                    style={{
                                      marginRight: '10px',
                                      fontWeight: 700,
                                    }}>
                                    {index + 1}.
                                  </span>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div
                                      style={{
                                        whiteSpace: 'nowrap',
                                        textOverflow: 'ellipsis',
                                        overflow: 'hidden',
                                        fontWeight: 600,
                                        color:
                                          indexCore === index
                                            ? '#1677FF'
                                            : '#1F1F1F',
                                      }}>
                                      {feedbackTitle || `Ý kiến ${index + 1}`}
                                    </div>
                                    {indexCore === index && (
                                      <div
                                        style={{
                                          marginTop: 6,
                                          color: '#1F1F1F',
                                          wordBreak: 'break-word',
                                          whiteSpace: 'normal',
                                        }}
                                        dangerouslySetInnerHTML={{
                                          __html: feedback.content,
                                        }}
                                      />
                                    )}
                                  </div>
                                </div>
                              </div>
                            </Menu.Item>
                          )
                        }
                      )}
                    </Menu>
                  </div>
                </ContentBlockWrapper>

                {/* Comment Section */}
                {selectedIndex && idIncludes?.includes(selectedIndex) && (
                  <FeedbackCommentSection
                    selectedIndex={selectedIndex}
                    feedbackCommentDataDetailV2={feedbackCommentDataDetailV2}
                    currentUser={currentUser}
                    departmentWithUserListv2={departmentWithUserListv2}
                    fileStore={fileStore}
                    FeedbackCommentStore={FeedbackCommentStore}
                    loadingAnimationStore={loadingAnimationStore}
                    EDITOR_REF={EDITOR_REF}
                    updateCurrentComment={updateCurrentComment}
                    documentNumber={feedbackDetailData?.documentNumber}
                    voterName={currentUser?.name_uppercase}
                    feedbackTab={currentFeedbackTab}
                  />
                )}

                <ContentBlockWrapper style={{ marginBottom: 20 }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontWeight: 500,
                      fontSize: 14,
                      borderTopLeftRadius: 8,
                      borderTopRightRadius: 8,
                      padding: '10px 16px',
                      color: '#fff',
                      backgroundColor: blue,
                      cursor: 'pointer',
                    }}
                    onClick={() => setIsProcessOpen(!isProcessOpen)}>
                    <span>Quá trình lấy ý kiến</span>
                    {isProcessOpen ? <CaretDownFilled /> : <CaretLeftFilled />}
                  </div>
                  <div
                    style={{
                      padding: '16px',
                    }}>
                    <div
                      style={{
                        overflow: 'hidden',
                        maxHeight: isProcessOpen ? '1000px' : '0',
                        opacity: isProcessOpen ? 1 : 0,
                        transition:
                          'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease',
                        padding: 16,
                      }}>
                      <div
                        style={{
                          maxWidth: '1200px',
                          whiteSpace: 'pre-wrap',
                          overflowWrap: 'break-word',
                        }}
                        dangerouslySetInnerHTML={{
                          __html: feedbackDetailData.otherContent,
                        }}></div>
                    </div>
                  </div>
                </ContentBlockWrapper>
              </BlockLeft>
              <BlockRight>
                {/* CHỈ HIỂN THỊ PHẦN BIỂU QUYẾT NẾU LÀ NGƯỜI XỬ LÝ */}
                {selectedIndex &&
                  idIncludes?.includes(selectedIndex) &&
                  canVote &&
                  !isOverdue && (
                    <ContentBlockWrapper style={{ marginBottom: 20 }}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          fontWeight: 500,
                          fontSize: 14,
                          borderTopLeftRadius: 8,
                          borderTopRightRadius: 8,
                          padding: '10px 16px',
                          color: '#fff',
                          backgroundColor: blue,
                          cursor: 'pointer',
                        }}>
                        <span>Biểu quyết</span>
                        <AccessControlAction
                          aclActionType={ACL_ACTION_TYPE.esign__HDQT}>
                          <Tag
                            onClick={handleOpenPopupSign}
                            style={{
                              borderRadius: 10,
                              cursor: 'pointer',
                              marginRight: 0,
                              width: '80px',
                              textAlign: 'center',
                            }}
                            color={'blue'}>
                            Ký số
                          </Tag>
                        </AccessControlAction>
                      </div>

                      <div
                        style={{
                          padding: '16px',
                        }}>
                        {voteOptions.map(option => (
                          <div
                            key={option.value}
                            style={{
                              marginBottom: '12px',
                              padding: '12px',
                              border:
                                selectedOptions[selectedIndex] === option.value
                                  ? `2px solid ${blue}`
                                  : '1px solid #d9d9d9',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              transition: 'all 0.3s',
                            }}
                            onClick={() => {
                              setSelectedOptions(prev => ({
                                ...prev,
                                [selectedIndex]:
                                  prev[selectedIndex] === option.value
                                    ? null
                                    : option.value,
                              }))
                            }}>
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                              }}>
                              <Checkbox
                                checked={
                                  selectedOptions[selectedIndex] ===
                                  option.value
                                }
                                onChange={() => {
                                  setSelectedOptions(prev => ({
                                    ...prev,
                                    [selectedIndex]:
                                      prev[selectedIndex] === option.value
                                        ? null
                                        : option.value,
                                  }))
                                }}
                                onClick={e => e.stopPropagation()}>
                                <span style={{ fontWeight: 500 }}>
                                  {option.label}
                                </span>
                              </Checkbox>
                              <span style={{ color: '#666' }}>
                                {option.count}/{totalPeopleCountv2} phiếu:{' '}
                                {option.percent}%
                              </span>
                            </div>
                          </div>
                        ))}
                        <Button
                          type="primary"
                          onClick={handleVoteSubmit}
                          loading={isVoting}
                          block
                          style={{
                            marginTop: '16px',
                          }}>
                          Gửi biểu quyết
                        </Button>
                      </div>
                    </ContentBlockWrapper>
                  )}
                {/* NẾU KHÔNG PHẢI NGƯỜI XỬ LÝ - CHỈ HIỂN THỊ THỐNG KÊ */}
                {selectedIndex &&
                  idIncludes?.includes(selectedIndex) &&
                  (!canVote || isOverdue) && (
                    <ContentBlockWrapper style={{ marginBottom: 20 }}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          fontWeight: 500,
                          fontSize: 14,
                          borderTopLeftRadius: 8,
                          borderTopRightRadius: 8,
                          padding: '10px 16px',
                          color: '#fff',
                          backgroundColor: blue,
                          cursor: 'pointer',
                        }}>
                        <span>Thống kê</span>
                        <span
                          style={{
                            fontSize: '12px',
                            color: '#666',
                            fontWeight: 'normal',
                          }}>
                          {currentVoteCounts.AGREE +
                            currentVoteCounts.DISAGREE +
                            (isDelegateTab ? 0 : currentVoteCounts.OTHER)}
                          /{totalPeopleCountv2}
                        </span>
                      </div>
                      <div
                        style={{
                          padding: '16px',
                        }}>
                        {/* Tỷ lệ đồng ý */}
                        <div style={{ marginBottom: '16px' }}>
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              marginBottom: '8px',
                            }}>
                            <span>Tán thành</span>
                            <span style={{ color: '#666' }}>
                              {currentVoteCounts.AGREE}/{totalPeopleCountv2}
                            </span>
                          </div>
                          <Progress
                            percent={voteOptions[0].percent}
                            showInfo={false}
                            strokeColor="green"
                            size={['100%', 8]}
                          />
                        </div>

                        {/* Không đồng ý */}
                        <div style={{ marginBottom: '16px' }}>
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              marginBottom: '8px',
                            }}>
                            <span>Không tán thành</span>
                            <span style={{ color: '#666' }}>
                              {currentVoteCounts.DISAGREE}/{totalPeopleCountv2}
                            </span>
                          </div>
                          <Progress
                            percent={voteOptions[1].percent}
                            showInfo={false}
                            strokeColor="red"
                            size={['100%', 8]}
                          />
                        </div>

                        {!isDelegateTab && (
                          <div style={{ marginBottom: '16px' }}>
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                marginBottom: '8px',
                              }}>
                              <span>Không có ý kiến</span>
                              <span style={{ color: '#666' }}>
                                {currentVoteCounts.OTHER}/{totalPeopleCountv2}
                              </span>
                            </div>
                            <Progress
                              percent={voteOptions[2].percent}
                              showInfo={false}
                              strokeColor="blue"
                              size={['100%', 8]}
                            />
                          </div>
                        )}
                      </div>
                    </ContentBlockWrapper>
                  )}

                {/* CÁC PHẦN KHÁC VẪN HIỂN THỊ BìNH THƯỜNG */}
                {selectedIndex && idIncludes?.includes(selectedIndex) && (
                  <>
                    {/* Ý kiến bạn biểu quyết - CHỈ HIỂN THỊ NẾU LÀ NGƯỜI XỬ LÝ */}
                    {canVote && (
                      <ContentBlockWrapper style={{ marginBottom: 20 }}>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            fontWeight: 500,
                            fontSize: 14,
                            borderTopLeftRadius: 8,
                            borderTopRightRadius: 8,
                            padding: '10px 16px',
                            color: '#fff',
                            backgroundColor: blue,
                            cursor: 'pointer',
                          }}>
                          <span>Ý kiến bạn biểu quyết</span>
                        </div>
                        <div
                          style={{
                            padding: '16px',
                          }}>
                          <DocumentInfoList>
                            {latestFeedbacks?.filter(
                              item =>
                                item.feedbackUsername === currentUser?.username
                            )[0]?.createdAt ? (
                              <>
                                <dt>Bạn Biểu quyết</dt>
                                <dd>
                                  Ngày:{' '}
                                  {formatDateTime(
                                    latestFeedbacks?.filter(
                                      item =>
                                        item.feedbackUsername ===
                                        currentUser?.username
                                    )[0].createdAt
                                  )}
                                </dd>
                              </>
                            ) : (
                              <>
                                <dt>Bạn Biểu quyết</dt>
                                <dd>Chưa có ngày biểu quyết</dd>
                              </>
                            )}
                            {latestFeedbacks?.filter(
                              item =>
                                item.feedbackUsername === currentUser?.username
                            )[0]?.updatedAt ? (
                              <>
                                <dt>Thay đổi</dt>
                                <dd>
                                  Ngày:{' '}
                                  {formatDateTime(
                                    latestFeedbacks?.filter(
                                      item =>
                                        item.feedbackUsername ===
                                        currentUser?.username
                                    )[0]?.updatedAt
                                  )}
                                </dd>
                              </>
                            ) : (
                              <>
                                <dt>Thay đổi</dt>
                                <dd>Chưa có ngày thay đổi</dd>
                              </>
                            )}
                            {latestFeedbacks?.filter(
                              item =>
                                item.feedbackUsername === currentUser?.username
                            )[0]?.voteType ? (
                              <>
                                <dt>Lựa chọn</dt>
                                <dd>
                                  {latestFeedbacks?.filter(
                                    item =>
                                      item.feedbackUsername ===
                                      currentUser?.username
                                  )[0]?.voteType === 'AGREE'
                                    ? 'Tán thành'
                                    : latestFeedbacks?.filter(
                                        item =>
                                          item.feedbackUsername ===
                                          currentUser?.username
                                      )[0]?.voteType === 'DISAGREE'
                                    ? 'Không tán thành'
                                    : 'Không có ý kiến'}
                                </dd>
                              </>
                            ) : (
                              <>
                                <dt>Lựa chọn</dt>
                                <dd>Chưa có lựa chọn</dd>
                              </>
                            )}
                          </DocumentInfoList>
                        </div>
                      </ContentBlockWrapper>
                    )}
                    <ContentBlockWrapper style={{ marginBottom: 20 }}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          fontWeight: 500,
                          fontSize: 14,
                          borderTopLeftRadius: 8,
                          borderTopRightRadius: 8,
                          padding: '10px 16px',
                          color: '#fff',
                          backgroundColor: blue,
                          cursor: 'pointer',
                        }}>
                        <span>Ý kiến biểu quyết (ký số)</span>
                      </div>

                      <div
                        style={{
                          padding: '16px',
                        }}>
                        <DocumentInfoList>
                          {filteredLatestFeedbacksAllContents.length > 0 ? (
                            filteredLatestFeedbacksAllContents.map(record => (
                              <Fragment key={record.fileId || record.id}>
                                <dt>
                                  {getNameUppercaseByUsername(
                                    record.feedbackUsername
                                  )}
                                </dt>
                                <dd>
                                  <FileItem
                                    file_id={record.fileId}
                                    file_name={record.fileName}
                                    file_type={utils.getExtensionFile(
                                      record.fileName
                                    )}
                                  />
                                </dd>
                              </Fragment>
                            ))
                          ) : (
                            <Fragment key={new Date().getTime()}>
                              <dt>Biểu quyết</dt>
                              <dd>Chưa có biểu quyết</dd>
                            </Fragment>
                          )}
                        </DocumentInfoList>
                      </div>
                    </ContentBlockWrapper>
                  </>
                )}

                {/* LUÔN HIỂN THỊ TÀI LIỆU ĐÍNH KÈM VÀ XỬ LÝ PHIẾU CỦA CẢ PHIẾU */}
                <ContentBlockWrapper style={{ marginBottom: 20 }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontWeight: 500,
                      fontSize: 14,
                      borderTopLeftRadius: 8,
                      borderTopRightRadius: 8,
                      padding: '10px 16px',
                      color: '#fff',
                      backgroundColor: blue,
                      cursor: 'pointer',
                    }}>
                    Tài liệu đính kèm
                  </div>
                  <div
                    style={{
                      padding: '16px',
                    }}>
                    <DocumentInfoList>
                      <dt>Tài liệu đính kèm</dt>
                      {allFileNames?.length > 0 ? (
                        <dd>
                          {allFileNames?.flatMap(group =>
                            group.map(fileName => (
                              <div style={{ marginBottom: '8px' }}>
                                <FileItem
                                  key={fileName.id}
                                  file_id={fileName.fileId?.replace(
                                    /[\[\]]/g,
                                    ''
                                  )}
                                  file_name={fileName.fileName}
                                  file_type={utils.getExtensionFile(
                                    fileName.fileName
                                  )}
                                />
                              </div>
                            ))
                          )}
                        </dd>
                      ) : (
                        <dd>Chưa có tài liệu đính kèm</dd>
                      )}
                    </DocumentInfoList>
                  </div>
                </ContentBlockWrapper>
                <ContentBlockWrapper>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontWeight: 500,
                      fontSize: 14,
                      borderTopLeftRadius: 8,
                      borderTopRightRadius: 8,
                      padding: '10px 16px',
                      color: '#fff',
                      backgroundColor: blue,
                      cursor: 'pointer',
                    }}>
                    <span>Xử lý phiếu</span>
                  </div>
                  <div
                    style={{
                      padding: '16px',
                    }}>
                    <UserListWrapper>
                      <div style={{ display: 'flex', flexGrow: 1 }}>
                        <b style={{ marginRight: '35px' }}>Người xử lý:</b>
                        {handlerAssignList?.length !== 0 ? (
                          <PopoverWatchTime
                            departWithUsers={departmentWithUserListv2}
                            userList={selectUserStore.convertListToDefault(
                              (handlerAssignList || [])?.filter(user => {
                                return (
                                  user.permission === ASSIGNEE_TYPE.HANDLER &&
                                  user.name_uppercase != null
                                )
                              })
                            )}
                            title={'Người xử lý'}
                            isFullViewer={true}
                            compared_date={VIEWLOG_DATE.internal_document_until}
                            typeId={feedbackId}
                            created_at={feedbackDetailData.createdAt}
                          />
                        ) : (
                          <EmptyText>Không có Người xử lý.</EmptyText>
                        )}
                      </div>
                    </UserListWrapper>
                    <UserListWrapper>
                      <div style={{ display: 'flex', flexGrow: 1 }}>
                        <b style={{ marginRight: '35px' }}>Ban KS/Ban KTNB:</b>
                        {combinerAssignList?.length !== 0 ? (
                          <PopoverWatchTime
                            departWithUsers={departmentWithUserListv2}
                            userList={selectUserStore.convertListToDefault(
                              (combinerAssignList || [])?.filter(user => {
                                return (
                                  user.permission === ASSIGNEE_TYPE.FOLLOWER &&
                                  user.name_uppercase != null
                                )
                              })
                            )}
                            title={'Danh sách Ý kiến của các tiểu ban'}
                            isFullViewer={true}
                            compared_date={VIEWLOG_DATE.internal_document_until}
                            typeId={feedbackId}
                            created_at={feedbackDetailData.createdAt}
                          />
                        ) : (
                          <EmptyText>Không có Ban KS/Ban KTNB.</EmptyText>
                        )}
                      </div>
                    </UserListWrapper>
                    <UserListWrapper>
                      <div style={{ display: 'flex', flexGrow: 1 }}>
                        <b style={{ marginRight: '35px' }}>Người theo dõi:</b>
                        {viewerAssignList?.length !== 0 ? (
                          <PopoverWatchTime
                            departWithUsers={departmentWithUserListv2}
                            userList={selectUserStore.convertListToDefault(
                              (viewerAssignList || [])?.filter(user => {
                                return (
                                  user.permission === ASSIGNEE_TYPE.COMBINER &&
                                  user.name_uppercase != null
                                )
                              })
                            )}
                            title={'Người theo dõi'}
                            isFullViewer={true}
                            compared_date={VIEWLOG_DATE.internal_document_until}
                            typeId={feedbackId}
                            created_at={feedbackDetailData.createdAt}
                          />
                        ) : (
                          <EmptyText>Không có Người theo dõi</EmptyText>
                        )}
                      </div>
                    </UserListWrapper>
                  </div>
                </ContentBlockWrapper>
              </BlockRight>
            </ContentWrapper>
          </div>
        </>
      </DashboardLayout>

      {isEditModalVisible && (
        <FeedbackBoardUpdate
          feedbackDetailData={feedbackDetailData}
          isVisible={isEditModalVisible}
          handleCancel={handleCloseEditModal}
          fetchFeedback={fetchFeedbackDetailData}
          setIsVisible={setIsEditModalVisible}
          assignees={feedbackDetailData.assignees}
          feedbackTab={currentFeedbackTab}
        />
      )}

      {isAddPersonModalVisible && (
        <FeedbackBoardAddPerson
          isVisible={isAddPersonModalVisible}
          handleCancel={handleCloseAddPersonModal}
          feedbackDetailData={feedbackDetailData}
          fetchFeedback={fetchFeedbackDetailData}
          setIsVisible={setIsAddPersonModalVisible}
        />
      )}

      {exportDocx && (
        <ExportDocx
          countAllUser={countAllUser}
          countAgree={countAgree}
          countDisAgree={countDisAgree}
          countOther={countOther}
          totalPeopleCount={totalPeopleCountv2}
          feedbackDetailData={feedbackDetailData}
          resultsVoting={results}
          finalUserList={exportUserList}
          positionByUsername={exportPositionByUsername}
          feedbackTab={currentFeedbackTab}
        />
      )}

      {isVisiblePopupSign && (
        <FeedbackSign
          isVisiblePopupSign={isVisiblePopupSign}
          handleClosePopupSign={() => {
            setIsVisiblePopupSign(false)
          }}
          hiddenSelectDocument
          fileId={fileIdWithCurrentUser}
          fileName={fileNameWithCurrentUser}
          handleAfterSign={handleAfterSign}
        />
      )}
      <div style={{ display: 'none' }}>
        <ExportPdf
          feedbackDetailData={feedbackDetailData}
          selectedIndex={selectedIndex}
          currentUser={currentUser}
          selectedOption={selectedOptions[selectedIndex]}
          feedbackTab={currentFeedbackTab}
        />
        <ExportPdfV2
          feedbackDetailData={feedbackDetailData}
          selectedOption={selectedOptions[selectedIndex]}
          comment={currentComment || getAllComments()}
          selectedIndex={selectedIndex}
          currentUser={currentUser}
          feedbackTab={currentFeedbackTab}
        />
      </div>
    </>
  )
}

export default inject(
  'selectUserStore',
  'documentAccessibilityStore',
  'selectPeopleStore',
  'loadingAnimationStore',
  'authenticationStore',
  'FeedbackProcedureStore',
  'FeedbackCommentStore',
  'departmentStore',
  'userStore',
  'selectUserStore',
  'electronicDocumentStore',
  'signedDocumentStore',
  'fileStore',
  'userGroupStore',
  'aclStore'
)(observer(FeedbackBoardDetail))
